import type { PreviewSimRunStatus } from "@/api/previewSim";
import {
  alignLabRuns,
  buildLabTurns,
  labCaptureWarning,
  type LabAlignedTurn,
  type LabTurn,
  type LabTurnDiff,
  type LabTurns,
} from "@/lib/labModel";
import {
  EVENT_STATUS_LABELS,
  TRACE_EVENT_KINDS,
  describeGap,
  describePosition,
  describeRoutingReason,
  resolveEvidence,
  type CriterionScalar,
  type Score,
  type TraceEvent,
  type TraceEventKind,
} from "@/lib/previewDecisions";
import {
  CAPTURE_STATE_LABELS,
  IDENTITY_AXES,
  IDENTITY_LABELS,
  OMISSION_REASON_LABELS,
  VIEW_LABELS,
  completenessOf,
  isAttested,
  type OmissionReason,
  type PreviewRunView,
  type RunIdentity,
} from "@/lib/previewInsights";
import { readPromptCall, type PromptCall } from "@/lib/previewPromptCall";
import { totalSimStats, type SimStatTotals } from "@/lib/previewSim";

/**
 * Pure model behind the Lab's run-level tabs. Everything is derived from the
 * run's compact event records (hashes and sizes only, never bodies), the run
 * view and the sim status. No React, no network, no clock: every time comes
 * from an event's own `occurredAt`, and an unparseable one is null, never
 * invented. Absence stays null (or an empty list), never 0.
 */

// ── Shared helpers ───────────────────────────────────────────────────────────

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function timeOf(value: string | null | undefined): number | null {
  if (typeof value !== "string") {
    return null;
  }
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
}

function round(value: number, digits: number): number {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
}

function mean(values: readonly number[]): number | null {
  return values.length === 0 ? null : values.reduce((a, b) => a + b, 0) / values.length;
}

function max(values: readonly number[]): number | null {
  return values.length === 0 ? null : Math.max(...values);
}

function sum(values: readonly number[]): number | null {
  return values.length === 0 ? null : values.reduce((a, b) => a + b, 0);
}

// ── (A) Overview ─────────────────────────────────────────────────────────────

export type LabMissingRange = {
  from: number | null;
  to: number | null;
  reason: OmissionReason | null;
  reasonLabel: string;
  sentence: string;
};

export type LabOmission = {
  eventRef: string | null;
  field: string | null;
  reason: OmissionReason | null;
  reasonLabel: string;
  sizeCapExceeded: boolean;
  restricted: boolean;
};

export type LabIdentityRow = {
  axis: keyof RunIdentity;
  label: string;
  value: string | null;
  attested: boolean;
};

export type LabOverview = {
  stateLabel: string;
  /** The capture state in words ("Not loaded" when the run view is not loaded). */
  captureState: string;
  captureWarning: string | null;
  eventCount: number;
  lastSequence: number | null;
  turnCount: number;
  completeThroughSequence: number | null;
  missingRanges: LabMissingRange[];
  omissions: LabOmission[];
  /** A missing range, an omission or the run's own failure code names size_cap_exceeded. */
  sizeCapExceeded: boolean;
  /** Null when no trace summary is loaded. */
  shortfallCount: number | null;
  restrictedCount: number | null;
  identity: LabIdentityRow[];
  simTotals: SimStatTotals | null;
};

function reasonLabel(reason: OmissionReason | null): string {
  return reason === null ? "reason not reported" : OMISSION_REASON_LABELS[reason];
}

function buildOverview(
  view: PreviewRunView | null,
  sim: PreviewSimRunStatus | null,
  lab: LabTurns,
  recordCount: number,
): LabOverview {
  const summary = view?.summary ?? null;
  const completeness = summary?.completeness ?? null;
  const missingRanges = (completeness?.missingRanges ?? []).map((range) => ({
    from: range.fromSequence,
    to: range.toSequence,
    reason: range.reason,
    reasonLabel: reasonLabel(range.reason),
    sentence: describeGap(range),
  }));
  const omissions = (completeness?.omissions ?? []).map((omission) => ({
    eventRef: omission.eventRef,
    field: omission.field,
    reason: omission.reason,
    reasonLabel: reasonLabel(omission.reason),
    sizeCapExceeded: omission.reason === "size_cap_exceeded",
    restricted: omission.reason === "restricted_technical",
  }));
  const reading = summary === null ? null : completenessOf(summary);
  return {
    stateLabel: view === null ? "Not loaded" : VIEW_LABELS[view.kind],
    captureState: view === null ? "Not loaded" : CAPTURE_STATE_LABELS[view.run.captureState],
    captureWarning: labCaptureWarning(view?.run ?? null),
    eventCount: summary?.eventCount ?? recordCount,
    lastSequence: summary?.lastSequence ?? view?.run.lastSequence ?? null,
    turnCount: lab.turns.length,
    completeThroughSequence: completeness?.completeThroughSequence ?? null,
    missingRanges,
    omissions,
    sizeCapExceeded:
      missingRanges.some((r) => r.reason === "size_cap_exceeded") ||
      omissions.some((o) => o.sizeCapExceeded) ||
      view?.run.failureCode === "size_cap_exceeded",
    shortfallCount: reading?.shortfallOmissionCount ?? null,
    restrictedCount: reading?.restrictedOmissionCount ?? null,
    identity: IDENTITY_AXES.map((axis) => {
      const value = summary?.identity[axis] ?? null;
      return { axis, label: IDENTITY_LABELS[axis], value, attested: isAttested(value) };
    }),
    simTotals: sim === null ? null : totalSimStats(sim.simStats),
  };
}

// ── (B) Timeline ─────────────────────────────────────────────────────────────

export type LabRun = {
  label: string | null;
  fromTurn: number;
  toTurn: number;
  turnCount: number;
  startedAt: string | null;
  endedAt: string | null;
  durationMs: number | null;
};

export type LabMarker = {
  turnNumber: number;
  sequence: number;
  from: string;
  to: string;
  reasonCode: string | null;
  reasonLabel: string;
  eventRef: string;
};

export type LabEventRow = {
  eventRef: string;
  sequence: number;
  turnNumber: number | null;
  kind: TraceEventKind;
  family: TraceEvent["family"];
  label: string;
  status: TraceEvent["status"];
  statusLabel: string;
  occurredAt: string | null;
};
export type LabAttentionRow = LabEventRow;

export type LabTimeline = {
  agentRuns: LabRun[];
  trackRuns: LabRun[];
  markers: LabMarker[];
  attention: LabAttentionRow[];
  preTurn: LabEventRow[];
  unreadable: LabEventRow[];
  unreadableRecordCount: number;
};

function turnNumberByRef(lab: LabTurns): Map<string, number> {
  const byRef = new Map<string, number>();
  for (const turn of lab.turns) {
    for (const event of turn.events) {
      byRef.set(event.eventRef, turn.turnNumber);
    }
  }
  return byRef;
}

function eventRowOf(event: TraceEvent, turnByRef: ReadonlyMap<string, number>): LabEventRow {
  return {
    eventRef: event.eventRef,
    sequence: event.sequence,
    turnNumber: turnByRef.get(event.eventRef) ?? null,
    kind: event.kind,
    family: event.family,
    label:
      event.family === "unreadable_detail"
        ? `${event.label} (unreadable detail: ${event.reason})`
        : event.label,
    status: event.status,
    statusLabel: EVENT_STATUS_LABELS[event.status],
    occurredAt: event.occurredAt,
  };
}

/** Consecutive turns sharing `labelOf` become one run; duration spans the run's earliest to latest parseable occurredAt. */
function runsBy(turns: readonly LabTurn[], labelOf: (turn: LabTurn) => string | null): LabRun[] {
  const groups: LabTurn[][] = [];
  for (const turn of turns) {
    const last = groups[groups.length - 1];
    if (last !== undefined && labelOf(last[0]!) === labelOf(turn)) {
      last.push(turn);
    } else {
      groups.push([turn]);
    }
  }
  return groups.map((group) => {
    const stamps = group
      .flatMap((turn) => turn.events.map((event) => event.occurredAt))
      .map((at) => ({ at, time: timeOf(at) }))
      .filter((s): s is { at: string; time: number } => s.time !== null);
    const first = stamps.reduce<(typeof stamps)[number] | null>(
      (best, s) => (best === null || s.time < best.time ? s : best),
      null,
    );
    const last = stamps.reduce<(typeof stamps)[number] | null>(
      (best, s) => (best === null || s.time > best.time ? s : best),
      null,
    );
    return {
      label: labelOf(group[0]!),
      fromTurn: group[0]!.turnNumber,
      toTurn: group[group.length - 1]!.turnNumber,
      turnCount: group.length,
      startedAt: first?.at ?? null,
      endedAt: last?.at ?? null,
      durationMs: first !== null && last !== null ? last.time - first.time : null,
    };
  });
}

function buildTimeline(lab: LabTurns): LabTimeline {
  const turnByRef = turnNumberByRef(lab);
  const rowOf = (event: TraceEvent) => eventRowOf(event, turnByRef);
  const markers = lab.turns.flatMap((turn) =>
    turn.transitions.map(
      (event): LabMarker => ({
        turnNumber: turn.turnNumber,
        sequence: event.sequence,
        from: describePosition(event.detail.from),
        to: describePosition(event.detail.to),
        reasonCode: event.detail.reasonCode,
        reasonLabel:
          event.detail.reasonCode === null
            ? "no reason recorded"
            : describeRoutingReason(event.detail.reasonCode),
        eventRef: event.eventRef,
      }),
    ),
  );
  return {
    agentRuns: runsBy(lab.turns, (turn) => turn.agent),
    trackRuns: runsBy(lab.turns, (turn) => turn.track),
    markers,
    attention: lab.allEvents
      .filter(
        (event) =>
          event.family === "unreadable_detail" ||
          event.status === "attention" ||
          event.status === "failed" ||
          event.status === "unavailable",
      )
      .map(rowOf),
    preTurn: lab.preTurnEvents.map(rowOf),
    unreadable: lab.allEvents.filter((event) => event.family === "unreadable_detail").map(rowOf),
    unreadableRecordCount: lab.unreadable,
  };
}

// ── (C) Flow graph ───────────────────────────────────────────────────────────

export const FLOW_COLUMNS = 4;
export const FLOW_CELL_WIDTH = 180;
export const FLOW_CELL_HEIGHT = 110;
export const FLOW_NODE_WIDTH = 140;
export const FLOW_NODE_HEIGHT = 56;
const FLOW_PADDING = 20;

export type LabFlowMark = "pass" | "fail" | "mixed" | "none";

export type LabFlowNode = {
  id: string;
  agent: string | null;
  track: string | null;
  label: string;
  visits: number;
  firstTurn: number;
  turns: number[];
  passed: number;
  failed: number;
  mark: LabFlowMark;
  /** Top-left corner of the node box. */
  x: number;
  y: number;
};

export type LabFlowEdge = {
  id: string;
  /** 1-based traversal order. */
  order: number;
  from: string;
  to: string;
  turnNumber: number;
  reasonCode: string | null;
};

export type LabFlowGraph = {
  nodes: LabFlowNode[];
  edges: LabFlowEdge[];
  width: number;
  height: number;
};

export function labFlowNodeTurn(node: Pick<LabFlowNode, "firstTurn">): number {
  return node.firstTurn;
}

export function labFlowEdgeTurn(edge: Pick<LabFlowEdge, "turnNumber">): number {
  return edge.turnNumber;
}

function buildFlow(lab: LabTurns): LabFlowGraph {
  const nodes: LabFlowNode[] = [];
  const edges: LabFlowEdge[] = [];
  const byKey = new Map<string, LabFlowNode>();
  let previous: LabFlowNode | null = null;

  for (const turn of lab.turns) {
    const position = turn.after ?? turn.before;
    if (position === null) {
      continue;
    }
    const key = JSON.stringify([position.agent, position.track]);
    let node = byKey.get(key);
    if (node === undefined) {
      const index = nodes.length;
      const label =
        [...new Set([position.agent, position.track].filter((p): p is string => p !== null))].join(
          " · ",
        ) || "no position recorded";
      node = {
        id: `n${index + 1}`,
        agent: position.agent,
        track: position.track,
        label,
        visits: 0,
        firstTurn: turn.turnNumber,
        turns: [],
        passed: 0,
        failed: 0,
        mark: "none",
        x: FLOW_PADDING + (index % FLOW_COLUMNS) * FLOW_CELL_WIDTH,
        y: FLOW_PADDING + Math.floor(index / FLOW_COLUMNS) * FLOW_CELL_HEIGHT,
      };
      byKey.set(key, node);
      nodes.push(node);
    }
    node.turns.push(turn.turnNumber);
    node.visits += 1;
    for (const criterion of turn.criteria) {
      if (criterion.detail.passed) {
        node.passed += 1;
      } else {
        node.failed += 1;
      }
    }
    if (previous !== null && previous !== node) {
      edges.push({
        id: `e${edges.length + 1}`,
        order: edges.length + 1,
        from: previous.id,
        to: node.id,
        turnNumber: turn.turnNumber,
        reasonCode: turn.transitions[0]?.detail.reasonCode ?? null,
      });
    }
    previous = node;
  }

  for (const node of nodes) {
    node.mark =
      node.failed === 0 && node.passed > 0
        ? "pass"
        : node.passed === 0 && node.failed > 0
          ? "fail"
          : node.passed > 0 && node.failed > 0
            ? "mixed"
            : "none";
  }

  const columns = Math.min(nodes.length, FLOW_COLUMNS);
  const rows = Math.ceil(nodes.length / FLOW_COLUMNS);
  return {
    nodes,
    edges,
    width: columns === 0 ? 0 : (columns - 1) * FLOW_CELL_WIDTH + FLOW_NODE_WIDTH + 2 * FLOW_PADDING,
    height: rows === 0 ? 0 : (rows - 1) * FLOW_CELL_HEIGHT + FLOW_NODE_HEIGHT + 2 * FLOW_PADDING,
  };
}

// ── (D) Prompt rows ──────────────────────────────────────────────────────────

export type LabPromptRow = {
  eventRef: string;
  /** Null when the record carried no valid sequence. */
  sequence: number | null;
  turnNumber: number | null;
  agent: string | null;
  phase: string | null;
  track: string | null;
  step: string | null;
  callType: string | null;
  /** callType, or 'unknown' when the call names none. */
  family: string;
  outcome: "invoked" | "not_invoked" | "failed";
  reasonCode: string | null;
  latencyMs: number | null;
  model: string | null;
  deployment: string | null;
  status: PromptCall["status"];
  label: string | null;
  call: PromptCall;
};

/** One row from a call already read by readPromptCall (also used for server-listed calls). */
export function promptRowOf(call: PromptCall): LabPromptRow {
  return {
    eventRef: call.eventRef,
    sequence: call.sequence,
    turnNumber: call.coordinate.turnNumber,
    agent: call.coordinate.agent,
    phase: call.coordinate.phase,
    track: call.coordinate.track,
    step: call.coordinate.step,
    callType: call.callType,
    family: call.callType ?? "unknown",
    outcome: call.invocation.outcome,
    reasonCode: call.invocation.reasonCode,
    latencyMs: call.invocation.latencyMs,
    model: call.modelIdentity?.model ?? null,
    deployment: call.modelIdentity?.modelDeployment ?? null,
    status: call.status,
    label: call.label,
    call,
  };
}

function byPromptOrder(a: LabPromptRow, b: LabPromptRow): number {
  return (
    (a.sequence ?? Number.MAX_SAFE_INTEGER) - (b.sequence ?? Number.MAX_SAFE_INTEGER) ||
    (a.eventRef < b.eventRef ? -1 : a.eventRef > b.eventRef ? 1 : 0)
  );
}

function buildPrompts(
  records: readonly unknown[],
  turnByRef: ReadonlyMap<string, number>,
): { prompts: LabPromptRow[]; promptUnreadable: number } {
  const prompts: LabPromptRow[] = [];
  let promptUnreadable = 0;
  for (const record of records) {
    if (!isRecord(record) || record["kind"] !== "prompt_call") {
      continue;
    }
    const reading = readPromptCall(record);
    if (reading.kind === "unreadable") {
      promptUnreadable += 1;
      continue;
    }
    const row = promptRowOf(reading.call);
    prompts.push({ ...row, turnNumber: row.turnNumber ?? turnByRef.get(row.eventRef) ?? null });
  }
  return { prompts: prompts.sort(byPromptOrder), promptUnreadable };
}

function buildFamilies(prompts: readonly LabPromptRow[]): { family: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const row of prompts) {
    counts.set(row.family, (counts.get(row.family) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([family, count]) => ({ family, count }))
    .sort((a, b) => b.count - a.count || (a.family < b.family ? -1 : a.family > b.family ? 1 : 0));
}

// ── (E) Latency & models ─────────────────────────────────────────────────────

export type LabFamilyLatency = {
  family: string;
  calls: number;
  invoked: number;
  notInvoked: number;
  failed: number;
  /** Null (never 0) when no invoked call of the family carried a latency. */
  totalMs: number | null;
  meanMs: number | null;
  maxMs: number | null;
};

export type LabTurnLatency = {
  turnNumber: number;
  calls: number;
  totalMs: number | null;
  maxMs: number | null;
};

export type LabLatencyModel = {
  byFamily: LabFamilyLatency[];
  byTurn: LabTurnLatency[];
  maxCallMs: number | null;
  models: { model: string | null; deployment: string | null; calls: number }[];
  notInvoked: { family: string; reasonCode: string | null; count: number; label: string }[];
  sim: { turn: number; latencyMs: number; model: string }[];
};

/** Only an invoked call that reported a latency contributes to millisecond statistics. */
function latenciesOf(rows: readonly LabPromptRow[]): number[] {
  return rows
    .filter((row) => row.outcome === "invoked" && row.latencyMs !== null)
    .map((row) => row.latencyMs as number);
}

function buildLatency(prompts: readonly LabPromptRow[], sim: PreviewSimRunStatus | null): LabLatencyModel {
  const families = new Map<string, LabPromptRow[]>();
  const turns = new Map<number, LabPromptRow[]>();
  const models = new Map<string, { model: string | null; deployment: string | null; calls: number }>();
  const notInvoked = new Map<
    string,
    { family: string; reasonCode: string | null; count: number; label: string }
  >();
  for (const row of prompts) {
    families.set(row.family, [...(families.get(row.family) ?? []), row]);
    if (row.turnNumber !== null) {
      turns.set(row.turnNumber, [...(turns.get(row.turnNumber) ?? []), row]);
    }
    const modelKey = JSON.stringify([row.model, row.deployment]);
    const known = models.get(modelKey);
    if (known === undefined) {
      models.set(modelKey, { model: row.model, deployment: row.deployment, calls: 1 });
    } else {
      known.calls += 1;
    }
    if (row.outcome === "not_invoked") {
      const key = JSON.stringify([row.family, row.reasonCode]);
      const entry = notInvoked.get(key);
      if (entry === undefined) {
        notInvoked.set(key, {
          family: row.family,
          reasonCode: row.reasonCode,
          count: 1,
          label: `${row.family}: ${row.reasonCode ?? "no reason recorded"}`,
        });
      } else {
        entry.count += 1;
      }
    }
  }
  return {
    byFamily: [...families.entries()]
      .map(([family, rows]): LabFamilyLatency => {
        const ms = latenciesOf(rows);
        const average = mean(ms);
        return {
          family,
          calls: rows.length,
          invoked: rows.filter((r) => r.outcome === "invoked").length,
          notInvoked: rows.filter((r) => r.outcome === "not_invoked").length,
          failed: rows.filter((r) => r.outcome === "failed").length,
          totalMs: sum(ms),
          meanMs: average === null ? null : Math.round(average),
          maxMs: max(ms),
        };
      })
      .sort((a, b) => (a.family < b.family ? -1 : a.family > b.family ? 1 : 0)),
    byTurn: [...turns.entries()]
      .sort(([a], [b]) => a - b)
      .map(([turnNumber, rows]) => {
        const ms = latenciesOf(rows);
        return { turnNumber, calls: rows.length, totalMs: sum(ms), maxMs: max(ms) };
      }),
    maxCallMs: max(latenciesOf(prompts)),
    models: [...models.values()].sort((a, b) => b.calls - a.calls),
    notInvoked: [...notInvoked.values()],
    // Only the latency and model fields of the contract's PreviewSimTurnStat; no token fields.
    sim: (sim?.simStats ?? []).map((stat) => ({
      turn: stat.turn,
      latencyMs: stat.latencyMs,
      model: stat.model,
    })),
  };
}

// ── (F) Metrics ──────────────────────────────────────────────────────────────

export type LabMetrics = {
  turns: number;
  minutes: number | null;
  tracksVisited: string[];
  repeats: number;
  substepCounts: { track: string; passed: number; failed: number }[];
  meanReplyLatencyMs: number | null;
  textTurns: { turnNumber: number; eventRef: string }[];
};

const REPLY_CALL_TYPE = /conversation|reply|response/i;

/**
 * The reply family, mirroring Reliv's `call_type == "reply"`. Brain's real
 * traces (checked against run pvs_5e97...: callTypes conversation, extraction,
 * validation, gate, clinical_memory, classification) name the reply call
 * "conversation", and it is the one that carries a patientFacingOutput. So a
 * reply family is any family with at least one call carrying a
 * patientFacingOutput; a trace where none does falls back to the callType name.
 */
export function replyFamiliesOf(prompts: readonly LabPromptRow[]): Set<string> {
  const withOutput = new Set(
    prompts.filter((row) => row.call.patientFacingOutput !== null).map((row) => row.family),
  );
  return withOutput.size > 0
    ? withOutput
    : new Set(prompts.filter((row) => REPLY_CALL_TYPE.test(row.callType ?? "")).map((r) => r.family));
}

function buildMetrics(lab: LabTurns, prompts: readonly LabPromptRow[]): LabMetrics {
  const times = lab.allEvents.map((e) => timeOf(e.occurredAt)).filter((t): t is number => t !== null);

  const tracksVisited: string[] = [];
  for (const turn of lab.turns) {
    if (turn.agent !== null && tracksVisited[tracksVisited.length - 1] !== turn.agent) {
      tracksVisited.push(turn.agent);
    }
  }

  const seenHashes = new Set<string>();
  let repeats = 0;
  const textTurns: LabMetrics["textTurns"] = [];
  for (const turn of lab.turns) {
    const slot = turn.turnEvent?.detail.livResponse ?? null;
    if (turn.turnEvent === null || slot === null) {
      continue;
    }
    textTurns.push({ turnNumber: turn.turnNumber, eventRef: turn.turnEvent.eventRef });
    if (slot.contentHash !== null) {
      if (seenHashes.has(slot.contentHash)) {
        repeats += 1;
      }
      seenHashes.add(slot.contentHash);
    }
  }

  const substeps = new Map<string, { passed: number; failed: number }>();
  for (const event of lab.allEvents) {
    if (event.family !== "criterion_threshold") {
      continue;
    }
    const track = event.coordinate.track ?? "unknown track";
    const entry = substeps.get(track) ?? { passed: 0, failed: 0 };
    if (event.detail.passed) {
      entry.passed += 1;
    } else {
      entry.failed += 1;
    }
    substeps.set(track, entry);
  }

  const replyFamilies = replyFamiliesOf(prompts);
  const replyMean = mean(latenciesOf(prompts.filter((row) => replyFamilies.has(row.family))));

  return {
    turns: lab.turns.length,
    minutes: times.length < 2 ? null : round((Math.max(...times) - Math.min(...times)) / 60000, 2),
    tracksVisited,
    repeats,
    substepCounts: [...substeps.entries()].map(([track, counts]) => ({ track, ...counts })),
    meanReplyLatencyMs: replyMean === null ? null : Math.round(replyMean),
    textTurns,
  };
}

const ALLOWED_HOTLINES = new Set(["1201", "101", "100"]);

/** Reliv's per-reply text rules. The only metrics that need a body; the model never receives text. */
export function measureReplyText(text: string): {
  chars: number;
  questions: number;
  latinWords: number;
  strayNumbers: number;
} {
  return {
    chars: [...text].length,
    questions: (text.match(/[?؟]/g) ?? []).length,
    latinWords: (text.match(/[A-Za-z]{2,}/g) ?? []).length,
    strayNumbers: (text.match(/\b\d{3,4}\b/g) ?? []).filter((n) => !ALLOWED_HOTLINES.has(n)).length,
  };
}

export function summarizeTextMeasures(list: readonly ReturnType<typeof measureReplyText>[]): {
  avgReplyChars: number | null;
  questionsPerReply: number | null;
  languageLeaks: number | null;
  hotlineFlag: boolean | null;
} {
  if (list.length === 0) {
    return { avgReplyChars: null, questionsPerReply: null, languageLeaks: null, hotlineFlag: null };
  }
  const total = (pick: (m: (typeof list)[number]) => number) =>
    list.reduce((acc, m) => acc + pick(m), 0);
  return {
    avgReplyChars: round(total((m) => m.chars) / list.length, 1),
    questionsPerReply: round(total((m) => m.questions) / list.length, 2),
    languageLeaks: total((m) => m.latinWords),
    hotlineFlag: list.some((m) => m.strayNumbers > 0),
  };
}

// ── (G) Raw rows ─────────────────────────────────────────────────────────────

export type LabRawRow = {
  eventRef: string;
  sequence: number;
  turnNumber: number | null;
  kind: TraceEventKind;
  label: string;
  status: TraceEvent["status"];
  statusLabel: string;
  occurredAt: string | null;
  /** The compact record as the list read returned it (hashes and sizes only). */
  compact: unknown;
};

const RAW_LIMIT = 20_000;

/** Pretty JSON of a compact record; any string member named `inline` is replaced, and the output is capped. */
export function compactForDisplay(record: unknown): string {
  let text: string;
  try {
    text =
      JSON.stringify(
        record,
        (key, value: unknown) =>
          key === "inline" && typeof value === "string" ? "«body not loaded»" : value,
        2,
      ) ?? "undefined";
  } catch {
    return "The record could not be displayed.";
  }
  return text.length > RAW_LIMIT
    ? `${text.slice(0, RAW_LIMIT)}\n… truncated: showing the first ${RAW_LIMIT} of ${text.length} characters`
    : text;
}

// ── (H) Reference index ──────────────────────────────────────────────────────

export type LabRefEntry = {
  eventRef: string;
  kind: TraceEventKind;
  turnNumber: number | null;
  sequence: number;
  label: string;
};
export type LabRefIndex = ReadonlyMap<string, LabRefEntry>;

function buildRefs(
  lab: LabTurns,
  rawByRef: ReadonlyMap<string, unknown>,
  turnByRef: ReadonlyMap<string, number>,
): LabRefIndex {
  const refs = new Map<string, LabRefEntry>();
  for (const event of lab.allEvents) {
    refs.set(event.eventRef, {
      eventRef: event.eventRef,
      kind: event.kind,
      turnNumber: turnByRef.get(event.eventRef) ?? null,
      sequence: event.sequence,
      label: event.label,
    });
  }
  // Records readTraceEvent could not place still name their own reference.
  for (const [eventRef, record] of rawByRef) {
    if (refs.has(eventRef) || !isRecord(record)) {
      continue;
    }
    const kind = record["kind"];
    const sequence = record["sequence"];
    const label = record["label"];
    if (
      (TRACE_EVENT_KINDS as readonly unknown[]).includes(kind) &&
      typeof sequence === "number" &&
      Number.isInteger(sequence) &&
      typeof label === "string"
    ) {
      refs.set(eventRef, {
        eventRef,
        kind: kind as TraceEventKind,
        turnNumber: null,
        sequence,
        label,
      });
    }
  }
  return refs;
}

export type LabRefResolution =
  | {
      kind: "turn_event" | "prompt_call" | "event";
      eventRef: string;
      turnNumber: number | null;
      label: string;
      targetKind: TraceEventKind;
    }
  | { kind: "declared_lost"; reasonLabel: string }
  | { kind: "unresolved" };

const eventIndexCache = new WeakMap<readonly TraceEvent[], Map<string, TraceEvent>>();

function eventIndexOf(events: readonly TraceEvent[]): Map<string, TraceEvent> {
  let index = eventIndexCache.get(events);
  if (index === undefined) {
    index = new Map(events.map((event) => [event.eventRef, event]));
    eventIndexCache.set(events, index);
  }
  return index;
}

function resolvedOf(entry: LabRefEntry): LabRefResolution {
  return {
    kind: entry.kind === "turn" ? "turn_event" : entry.kind === "prompt_call" ? "prompt_call" : "event",
    eventRef: entry.eventRef,
    turnNumber: entry.turnNumber,
    label: entry.label,
    targetKind: entry.kind,
  };
}

/** Resolves a citation against the loaded events only (previewDecisions.resolveEvidence), plus the raw-record index. */
export function resolveLabRef(
  ref: string,
  citing: { eventRef: string; field: string; index?: number },
  model: Pick<LabRunModel, "refs" | "lab" | "view">,
): LabRefResolution {
  const resolution = resolveEvidence(
    ref,
    citing,
    eventIndexOf(model.lab.allEvents),
    model.view?.summary?.completeness ?? null,
  );
  if (resolution.kind === "resolved") {
    const { event } = resolution;
    return resolvedOf(
      model.refs.get(event.eventRef) ?? {
        eventRef: event.eventRef,
        kind: event.kind,
        turnNumber: null,
        sequence: event.sequence,
        label: event.label,
      },
    );
  }
  if (resolution.kind === "declared_lost") {
    return { kind: "declared_lost", reasonLabel: reasonLabel(resolution.reason) };
  }
  const entry = model.refs.get(ref);
  return entry !== undefined ? resolvedOf(entry) : { kind: "unresolved" };
}

export function labTurnForRef(model: Pick<LabRunModel, "refs">, ref: string): number | null {
  return model.refs.get(ref)?.turnNumber ?? null;
}

// ── The model ────────────────────────────────────────────────────────────────

export type LabRunModel = {
  previewRunRef: string;
  lab: LabTurns;
  view: PreviewRunView | null;
  sim: PreviewSimRunStatus | null;
  overview: LabOverview;
  timeline: LabTimeline;
  flow: LabFlowGraph;
  prompts: LabPromptRow[];
  promptFamilies: { family: string; count: number }[];
  /** prompt_call records readPromptCall could not read (counted, never listed). */
  promptUnreadable: number;
  latency: LabLatencyModel;
  metrics: LabMetrics;
  raw: LabRawRow[];
  refs: LabRefIndex;
};

export function buildLabRunModel(input: {
  previewRunRef: string;
  view: PreviewRunView | null;
  sim: PreviewSimRunStatus | null;
  records: readonly unknown[];
}): LabRunModel {
  const { previewRunRef, view, sim, records } = input;
  const lab = buildLabTurns(records);
  const turnByRef = turnNumberByRef(lab);

  const rawByRef = new Map<string, unknown>();
  for (const record of records) {
    if (isRecord(record) && typeof record["eventRef"] === "string") {
      rawByRef.set(record["eventRef"], record);
    }
  }

  const { prompts, promptUnreadable } = buildPrompts(records, turnByRef);
  return {
    previewRunRef,
    lab,
    view,
    sim,
    overview: buildOverview(view, sim, lab, records.length),
    timeline: buildTimeline(lab),
    flow: buildFlow(lab),
    prompts,
    promptFamilies: buildFamilies(prompts),
    promptUnreadable,
    latency: buildLatency(prompts, sim),
    metrics: buildMetrics(lab, prompts),
    raw: lab.allEvents.map((event) => ({
      eventRef: event.eventRef,
      sequence: event.sequence,
      turnNumber: turnByRef.get(event.eventRef) ?? null,
      kind: event.kind,
      label: event.label,
      status: event.status,
      statusLabel: EVENT_STATUS_LABELS[event.status],
      occurredAt: event.occurredAt,
      compact: rawByRef.get(event.eventRef) ?? null,
    })),
    refs: buildRefs(lab, rawByRef, turnByRef),
  };
}

export const EMPTY_LAB_RUN_MODEL: LabRunModel = buildLabRunModel({
  previewRunRef: "",
  view: null,
  sim: null,
  records: [],
});

// ── (I) Comparison ───────────────────────────────────────────────────────────

export type LabCompareCriterionSide = {
  observed: CriterionScalar | null;
  operator: string;
  threshold: CriterionScalar | null;
  passed: boolean;
};

export type LabCompareWhyNotSide = {
  disposition: string;
  reasonCode: string;
  comparedScore: Score | null;
};

export type LabCompareTurn = {
  turnNumber: number;
  onlyIn: "a" | "b" | null;
  signatureDiff: LabTurnDiff | null;
  criteria: {
    criterionId: string;
    a: LabCompareCriterionSide | null;
    b: LabCompareCriterionSide | null;
    differs: boolean;
  }[];
  whyNot: {
    alternative: string;
    a: LabCompareWhyNotSide | null;
    b: LabCompareWhyNotSide | null;
    differs: boolean;
  }[];
  /** `differs` flags a different CALL COUNT; latency is shown but never flagged (it always varies). */
  promptCalls: {
    a: { count: number; totalMs: number | null };
    b: { count: number; totalMs: number | null };
    differs: boolean;
  };
  positions: { a: string | null; b: string | null; differs: boolean };
};

export type LabCompareSummaryRow = { label: string; a: string; b: string; differs: boolean };

export type LabRunComparison = { rows: LabCompareTurn[]; summary: LabCompareSummaryRow[] };

function sameJson(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function idsOf<T>(left: readonly T[], right: readonly T[], idOf: (item: T) => string): string[] {
  return [...new Set([...left, ...right].map(idOf))];
}

function criterionSide(turn: LabTurn | null, criterionId: string): LabCompareCriterionSide | null {
  const event = turn?.criteria.find((c) => c.detail.criterionId === criterionId);
  if (event === undefined) {
    return null;
  }
  const { operator } = event.detail;
  return {
    observed: event.detail.observedValue,
    operator: typeof operator === "string" ? operator : operator.unknown,
    threshold: event.detail.threshold,
    passed: event.detail.passed,
  };
}

function whyNotSide(turn: LabTurn | null, alternative: string): LabCompareWhyNotSide | null {
  const event = turn?.whyNots.find((w) => w.detail.alternative === alternative);
  return event === undefined
    ? null
    : {
        disposition: event.detail.disposition,
        reasonCode: event.detail.reasonCode,
        comparedScore: event.detail.comparedScore,
      };
}

function promptStats(model: LabRunModel, turnNumber: number): { count: number; totalMs: number | null } {
  const rows = model.prompts.filter((row) => row.turnNumber === turnNumber);
  return { count: rows.length, totalMs: sum(latenciesOf(rows)) };
}

function positionOf(turn: LabTurn | null): string | null {
  const position = turn?.after ?? turn?.before ?? null;
  return position === null ? null : describePosition(position);
}

function compareTurn(aligned: LabAlignedTurn, a: LabRunModel, b: LabRunModel): LabCompareTurn {
  const criteria = idsOf(aligned.a?.criteria ?? [], aligned.b?.criteria ?? [], (c) => c.detail.criterionId).map(
    (criterionId) => {
      const left = criterionSide(aligned.a, criterionId);
      const right = criterionSide(aligned.b, criterionId);
      return { criterionId, a: left, b: right, differs: !sameJson(left, right) };
    },
  );
  const whyNot = idsOf(aligned.a?.whyNots ?? [], aligned.b?.whyNots ?? [], (w) => w.detail.alternative).map(
    (alternative) => {
      const left = whyNotSide(aligned.a, alternative);
      const right = whyNotSide(aligned.b, alternative);
      return { alternative, a: left, b: right, differs: !sameJson(left, right) };
    },
  );
  const left = promptStats(a, aligned.turnNumber);
  const right = promptStats(b, aligned.turnNumber);
  const positions = { a: positionOf(aligned.a), b: positionOf(aligned.b) };
  return {
    turnNumber: aligned.turnNumber,
    onlyIn: aligned.onlyIn,
    signatureDiff: aligned.diff,
    criteria,
    whyNot,
    promptCalls: { a: left, b: right, differs: left.count !== right.count },
    positions: { ...positions, differs: positions.a !== positions.b },
  };
}

function criteriaTotals(model: LabRunModel): string {
  const all = model.lab.allEvents.filter((e) => e.family === "criterion_threshold");
  const passed = all.filter((e) => e.family === "criterion_threshold" && e.detail.passed).length;
  return `${passed} passed / ${all.length - passed} failed`;
}

function summaryRow(label: string, a: string, b: string): LabCompareSummaryRow {
  return { label, a, b, differs: a !== b };
}

function ms(value: number | null): string {
  return value === null ? "none recorded" : `${value} ms`;
}

function finalPosition(model: LabRunModel): string {
  const last = model.lab.turns[model.lab.turns.length - 1] ?? null;
  return positionOf(last) ?? "no position recorded";
}

function summaryOf(a: LabRunModel, b: LabRunModel): LabCompareSummaryRow[] {
  const gaps = (m: LabRunModel) => (m.view?.summary ? String(m.overview.missingRanges.length) : "not loaded");
  const cap = (m: LabRunModel) => (m.overview.sizeCapExceeded ? "Yes" : "No");
  const tracks = (m: LabRunModel) =>
    m.metrics.tracksVisited.length === 0 ? "none recorded" : m.metrics.tracksVisited.join(" → ");
  return [
    summaryRow("Turns", String(a.metrics.turns), String(b.metrics.turns)),
    summaryRow("Final position", finalPosition(a), finalPosition(b)),
    summaryRow("Tracks visited", tracks(a), tracks(b)),
    summaryRow("Criteria", criteriaTotals(a), criteriaTotals(b)),
    summaryRow("Prompt calls", String(a.prompts.length), String(b.prompts.length)),
    summaryRow("Mean reply latency", ms(a.metrics.meanReplyLatencyMs), ms(b.metrics.meanReplyLatencyMs)),
    summaryRow("Missing ranges", gaps(a), gaps(b)),
    summaryRow("Size cap hit", cap(a), cap(b)),
  ];
}

export function buildRunComparison(a: LabRunModel, b: LabRunModel): LabRunComparison {
  return {
    rows: alignLabRuns(a.lab.turns, b.lab.turns).map((aligned) => compareTurn(aligned, a, b)),
    summary: summaryOf(a, b),
  };
}

/** Turns that exist in one run only, or differ in signatures, criteria, why-not alternatives, call count or position. */
export function differingTurnCount(rows: readonly LabCompareTurn[]): number {
  return rows.filter(
    (row) =>
      row.onlyIn !== null ||
      row.signatureDiff?.any === true ||
      row.criteria.some((c) => c.differs) ||
      row.whyNot.some((w) => w.differs) ||
      row.promptCalls.differs ||
      row.positions.differs,
  ).length;
}
